import { message } from 'antd'
import AuthService from './AuthService';

export const API_BASE = process.env.REACT_APP_API_BASE || "/"
export const TRANSCRIBER_API_BASE = process.env.REACT_APP_TRANSCRIBER_API_BASE || "/"

enum REQUEST_METHODS {
    GET = "GET",
    POST = "POST",
    PUT = "PUT",
    DELETE = "DELETE",
}

class CustomError extends Error {
    response: any
    response_json: any
}

interface Dict<T> {
    [Key: string]: T;
}

export default class HTTPClient {
    static instance: HTTPClient | null = null;

    constructor() {
        this.getJSONResult = this.getJSONResult.bind(this);
        this.postDataForJSONResult = this.postDataForJSONResult.bind(this);
        this.putDataForJSONResult = this.putDataForJSONResult.bind(this)
        this.deleteForJSONResult = this.deleteForJSONResult.bind(this)
    }

    static get() {
        if (HTTPClient.instance === null) {
            console.log("Creating new HTTPClient instance");
            HTTPClient.instance = new HTTPClient();
            return HTTPClient.instance
        }
        return HTTPClient.instance
    }

    getAuthHeader() {
        return "Bearer " + AuthService.getToken()
    }

    getCookie(name) {
        let cookie: Dict<string> = {};
        document.cookie.split(';').forEach(function (el) {
            let [k, v] = el.split('=');
            cookie[k.trim()] = v;
        })
        return cookie[name];
    }

    getURL(path: string, query = {}, api_base = API_BASE) {
        let url;
        if (process.env.NODE_ENV === 'development' || api_base === "/") {
            api_base = "/";
            let searchParams = new URLSearchParams()
            Object.keys(query).forEach(key => searchParams.append(key, query[key]))
            url = searchParams.toString()
                ? path + "?" + searchParams.toString()
                : path
        } else {
            url = HTTPClient.createURL(api_base, path, query)
        }
        return url
    }

    static createURL(api_base, path = "", query = {}) {
        let url = new URL(api_base! + path)
        Object.keys(query).forEach(key => url.searchParams.append(key, query[key]))
        return url
    }

    async performJSONRequest(
        { path, query = {}, includeAuth = true, body = {}, method = "GET", token = "", api_base }: {
            path: string,
            query?: {},
            includeAuth?: boolean | null,
            body?: {} | string,
            method?: "GET" | "POST" | "PUT" | "DELETE",
            api_base?: string,
            token?: string,
        }) {
        try {
            let headers: Dict<string> = {
                "Content-Type": "application/json",
                "Accept": "application/json",
            }
            if (includeAuth || token) {
                headers["Authorization"] = token || this.getAuthHeader()!
            }
            let _body: any = null
            if (method === REQUEST_METHODS.GET) {
                _body = null
            } else if (body instanceof FormData) {
                delete headers["Content-Type"]
                _body = body
            } else if (typeof body === 'object' && body !== null) {//the body could be a file
                _body = JSON.stringify(body)
            }
            let response = await fetch(this.getURL(path, query, api_base), { method, headers, body: _body })
            if (response.status < 200 || response.status > 299) {
                if (response.status === 401) {
                    AuthService.logOut()
                }
                let error = new CustomError(`${response.status} ${response.statusText}`)
                error.response = response
                try {
                    error.response_json = await response.json()
                    if (error.response_json?.message) {
                        error.message = error.response_json?.message
                        if (error.message === "auth_token does not match") {
                            message.error(error.message)
                            AuthService.logOut()
                        }
                    }
                } catch (e) {
                    console.error("Error parsing response body as json", e)
                }
                throw error
            }
            let response_json: any = null
            try {
                response_json = await response.json()
            } catch (e) {
                response_json = {}
            }
            if (response_json.status === "failed") {
                throw new CustomError(response_json.message)
            }
            return response_json
        } catch (error) {
            console.error(error)
            throw error
        }
    }

    async getJSONResult(path, query = {}) {
        return this.performJSONRequest({ path, query, includeAuth: true })
    }

    async getJSONResultWithoutAuth(path, query = {}) {
        return this.performJSONRequest({ path, query, includeAuth: false })
    }

    async postDataForJSONResult(path, body = {}, includeAuth = true) {
        return this.performJSONRequest({ path, query: {}, includeAuth, body, method: REQUEST_METHODS.POST })
    }

    async postDataForTranscriberJSONResult(path, body = {}) {
        return this.performJSONRequest({ path, body, includeAuth: true, api_base: TRANSCRIBER_API_BASE, method: REQUEST_METHODS.POST })
    }

    async postDataWithoutAuthForJSONResult(path, body = {}) {
        return this.postDataForJSONResult(path, body, false)
    }

    async putDataForJSONResult(path, body = {}) {
        return this.performJSONRequest({ path, includeAuth: true, body, method: REQUEST_METHODS.PUT })
    }

    async deleteForJSONResult(path, query, body = {}) {
        return this.performJSONRequest({ path, query, body, method: REQUEST_METHODS.DELETE })
    }
}